import { ChevronRightIcon, SidebarCloseIcon, SidebarOpenIcon } from 'lucide-react';
import { forwardRef, useMemo, useState } from 'react';
import { Link, LinkProps, useLocation, useNavigate } from 'react-router';

import { AccessContext } from '@invenco/common-interface/auth';

import LogoIcon from '@/assets/lynk-icon.svg?react';
import LogoName from '@/assets/lynk-name.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/core/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/core/sidebar';
import { cn } from '@/shared/helpers';
import { useFeatureFlags } from '@/shared/providers/FeatureFlagProvider';

import { AccountMenu } from './account-menu/account-menu';
import { ITEMS_BY_CONTEXT, NavItem, NavSubItem } from './items';
import { UserMenu } from './user-menu';

const LOADING_SKELETON_COUNT = 5;

type Props = {
  accessContext: AccessContext | undefined;
};

type ParentNavItemWithState = Omit<NavItem, 'children'> & {
  isActive: boolean;
  children: (NavSubItem & { isActive: boolean })[];
};
type NavItemWithState =
  | ParentNavItemWithState
  | (Omit<NavItem, 'children'> & {
      isActive: boolean;
      children: undefined;
    });

const isURLMatching = (pathname: string, item: { url?: string }) =>
  // strips any default query params defined in the nav item
  item.url ? pathname.startsWith(new URL(item.url, window.location.origin).pathname) : false;

const ItemLink = forwardRef<
  React.ComponentRef<typeof Link>,
  { item: NavItem | NavSubItem } & Omit<LinkProps, 'to' | 'onClick'>
>(({ item, ...props }, ref) => {
  const { isMobile, toggleSidebar } = useSidebar();
  return (
    <Link {...props} ref={ref} to={item.url ?? ''} onClick={isMobile ? toggleSidebar : undefined}>
      {'icon' in item && <item.icon />}
      <span>{item.title}</span>
    </Link>
  );
});

function CollapsibleMenuItem({ item }: { item: ParentNavItemWithState }) {
  const navigate = useNavigate();
  const { open: sidebarOpen } = useSidebar();
  const hasActiveChildren = item.children.some((child) => child.isActive);
  const [isOpen, setIsOpen] = useState(item.isActive || hasActiveChildren);
  return (
    <Collapsible
      asChild
      key={item.title}
      className="group/collapsible"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={hasActiveChildren ? !sidebarOpen || !isOpen : item.isActive}
            onClick={() => {
              const firstChildUrl = item.children[0].url;
              if (firstChildUrl && !sidebarOpen) {
                void navigate(firstChildUrl);
              }
            }}
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton asChild isActive={subItem.isActive}>
                  <ItemLink item={subItem} />
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function NavigationHeader() {
  const { isMobile, open, toggleSidebar } = useSidebar();
  return (
    <SidebarHeader className="py-0">
      <div
        className={cn(
          'group/logo flex px-2 transition-[colors,padding] group-data-[collapsible=icon]:px-0',
          open ? 'h-16 items-center justify-between' : 'flex-col gap-2 px-0 *:first:h-16',
        )}
      >
        <div aria-label="Lynk" className="flex items-center gap-2">
          <LogoIcon className="size-8 shrink-0 rotate-0 transition-transform ease-in-out dark:rotate-720" />
          <LogoName className="h-6 w-auto group-data-[collapsible=icon]:hidden" />
        </div>
        {!isMobile && (
          <SidebarMenuButton
            onClick={toggleSidebar}
            aria-label={`${open ? 'Close' : 'Open'} sidebar`}
            className="-mr-2 size-8"
            tooltip={`${open ? 'Close' : 'Open'} sidebar`}
          >
            {open ? <SidebarCloseIcon /> : <SidebarOpenIcon />}
          </SidebarMenuButton>
        )}
      </div>
      <AccountMenu />
    </SidebarHeader>
  );
}

export function NavigationSidebar({ accessContext }: Props) {
  const location = useLocation();
  const { getFeatureFlag } = useFeatureFlags();
  const items = useMemo<(NavItemWithState | ParentNavItemWithState)[]>(
    () =>
      (accessContext ? ITEMS_BY_CONTEXT[accessContext] : [])
        .filter((item) => !item.featureFlag || getFeatureFlag(item.featureFlag))
        .map((item) => ({
          ...item,
          isActive: isURLMatching(location.pathname, item),
          children: item.children
            ? item.children.map((subItem) => ({
                ...subItem,
                isActive: isURLMatching(location.pathname, subItem),
              }))
            : undefined,
        })),
    [accessContext, location.pathname, getFeatureFlag],
  );

  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      className="**:data-[slot=skeleton]:bg-sidebar-accent"
    >
      <NavigationHeader />
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {accessContext
              ? items.map((item) =>
                  item.children ? (
                    <CollapsibleMenuItem key={item.title} item={item} />
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title} isActive={item.isActive}>
                        <ItemLink item={item} />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ),
                )
              : Array.from({ length: LOADING_SKELETON_COUNT }, (_, index) => (
                  <SidebarMenuItem key={`skeleton-${index}`}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <UserMenu />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
